/**
 * The six starter templates (TikZ step 3, 1b). Each is small, commented, and
 * written in theme colour names. A template is a starting point, not a link:
 * after picking, it is ordinary TikZ and nothing points back here.
 */
import type { PreambleNeed } from "./tikzAuthoring";

export type TikzTemplateId = "commutative" | "flowchart" | "plot" | "tree" | "graph" | "blank";

export interface TikzTemplate {
  id: TikzTemplateId;
  name: string;
  desc: string;
  /** The libraries, as the card shows them. */
  libs: string;
  needs: PreambleNeed[];
  source: string;
  /** The card's picture, an SVG data URL. */
  thumb: string;
}

const K = "#2F2E2C";
const A = "#0A76A4";
const svg = (body: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 100'><defs><marker id='m' viewBox='0 0 10 10' refX='9' refY='5' markerWidth='6' markerHeight='6' orient='auto'><path d='M0,1 L10,5 L0,9 z' fill='${K}'/></marker></defs>${body}</svg>`,
  )}`;

const line = (x1: number, y1: number, x2: number, y2: number, extra = "") =>
  `<line x1='${x1}' y1='${y1}' x2='${x2}' y2='${y2}' stroke='${K}' stroke-width='1.2'${extra}/>`;

export const TIKZ_TEMPLATES: readonly TikzTemplate[] = [
  {
    id: "commutative",
    name: "Commutative diagram",
    desc: "Objects and arrows, with labels.",
    libs: "tikz-cd",
    needs: [{ kind: "package", name: "tikz-cd" }],
    source: [
      "% A commutative triangle. Arrow directions: r = right, d = down.",
      "\\begin{tikzcd}[column sep=large, row sep=large]",
      "  A \\arrow[r, \"f\"] \\arrow[dr, \"g \\circ f\"', lilia-accent]",
      "    & B \\arrow[d, \"g\"] \\\\",
      "    & C",
      "\\end{tikzcd}",
    ].join("\n"),
    thumb: svg(
      `<g font-family='Georgia,serif' font-style='italic' font-size='15' fill='${K}' text-anchor='middle'><text x='30' y='26'>A</text><text x='130' y='26'>B</text><text x='130' y='90'>C</text></g>` +
        line(42, 21, 116, 21, " marker-end='url(#m)'") +
        line(130, 32, 130, 74, " marker-end='url(#m)'") +
        `<line x1='40' y1='30' x2='118' y2='78' stroke='${A}' stroke-width='1.4'/>`,
    ),
  },
  {
    id: "flowchart",
    name: "Flowchart",
    desc: "Steps, a decision, arrows between them.",
    libs: "shapes.geometric · positioning",
    needs: [
      { kind: "library", name: "shapes.geometric" },
      { kind: "library", name: "positioning" },
    ],
    source: [
      "% A flowchart: steps, one decision, and the arrows between them.",
      "\\begin{tikzpicture}[",
      "    node distance=8mm and 14mm, >=stealth,",
      "    stage/.style={rectangle, rounded corners, draw=lilia-ink, minimum width=24mm, minimum height=8mm},",
      "    decision/.style={diamond, aspect=2, draw=lilia-accent, fill=lilia-accent-soft},",
      "  ]",
      "  % The boxes, each placed relative to the one before",
      "  \\node[stage] (collect) {Collect data};",
      "  \\node[decision, below=of collect] (check) {Clean?};",
      "  \\node[stage, below=of check] (analyse) {Analyse};",
      "  \\node[stage, right=of check] (fix) {Fix it};",
      "  % The arrows",
      "  \\draw[->] (collect) -- (check);",
      "  \\draw[->] (check) -- node[right] {yes} (analyse);",
      "  \\draw[->] (check) -- node[above] {no} (fix);",
      "  \\draw[->] (fix) |- (collect);",
      "\\end{tikzpicture}",
    ].join("\n"),
    thumb: svg(
      `<rect x='55' y='4' width='50' height='16' rx='4' fill='none' stroke='${K}' stroke-width='1.3'/>` +
        line(80, 20, 80, 34, " marker-end='url(#m)'") +
        `<path d='M80,36 L100,50 L80,64 L60,50 z' fill='none' stroke='${A}' stroke-width='1.4'/>` +
        line(80, 64, 80, 78, " marker-end='url(#m)'") +
        `<rect x='55' y='80' width='50' height='16' rx='4' fill='none' stroke='${K}' stroke-width='1.3'/>` +
        line(100, 50, 134, 50, " marker-end='url(#m)'") +
        `<rect x='136' y='42' width='20' height='16' rx='3' fill='none' stroke='${K}' stroke-width='1.3'/>`,
    ),
  },
  {
    id: "plot",
    name: "Function plot",
    desc: "Axes, a curve, ticks and a legend.",
    libs: "pgfplots",
    needs: [{ kind: "package", name: "pgfplots" }],
    source: [
      "% A function plot: axes, two curves and a legend.",
      "\\begin{tikzpicture}",
      "  \\begin{axis}[",
      "      width=8cm, height=5cm,",
      "      axis lines=left,",
      "      xlabel={$x$}, ylabel={$y$},",
      "      legend pos=north west,",
      "    ]",
      "    % A curve from a formula, in the theme's accent",
      "    \\addplot[lilia-accent, thick, domain=-3:3, samples=100] {1/(1+exp(-2*x))};",
      "    \\addlegendentry{$\\sigma(2x)$}",
      "    % A second one, dashed",
      "    \\addplot[lilia-ink, dashed, domain=-3:3] {0.5 - 0.1*x};",
      "    \\addlegendentry{$0.5 - 0.1x$}",
      "  \\end{axis}",
      "\\end{tikzpicture}",
    ].join("\n"),
    thumb: svg(
      line(16, 88, 152, 88, " marker-end='url(#m)'") +
        line(22, 94, 22, 6, " marker-end='url(#m)'") +
        `<path d='M22,82 C62,80 82,24 148,14' fill='none' stroke='${A}' stroke-width='1.8'/>` +
        `<path d='M22,60 C70,62 100,70 148,74' fill='none' stroke='${K}' stroke-width='1.3' stroke-dasharray='4 3'/>` +
        [50, 78, 106, 134].map((x) => `<line x1='${x}' y1='86' x2='${x}' y2='90' stroke='${K}'/>`).join(""),
    ),
  },
  {
    id: "tree",
    name: "Tree",
    desc: "A rooted tree: parse trees, hierarchies.",
    libs: "trees (built in)",
    needs: [],
    source: [
      "% A rooted tree. Each child is a subtree; the spacing is set once, per level.",
      "\\begin{tikzpicture}[",
      "    level distance=12mm,",
      "    level 1/.style={sibling distance=30mm},",
      "    level 2/.style={sibling distance=14mm},",
      "    every node/.style={circle, draw=lilia-ink, minimum size=7mm},",
      "  ]",
      "  \\node[draw=lilia-accent] {S}",
      "    child { node {NP}",
      "      child { node {D} }",
      "      child { node {N} } }",
      "    child { node {VP}",
      "      child { node {V} } };",
      "\\end{tikzpicture}",
    ].join("\n"),
    thumb: svg(
      [
        [80, 12, 45, 48],
        [80, 12, 115, 48],
        [45, 48, 28, 86],
        [45, 48, 62, 86],
        [115, 48, 115, 86],
      ]
        .map(([a, b, c, d]) => line(a, b, c, d))
        .join("") +
        [
          [80, 12, A],
          [45, 48, K],
          [115, 48, K],
          [28, 86, K],
          [62, 86, K],
          [115, 86, K],
        ]
          .map(([x, y, c]) => `<circle cx='${x}' cy='${y}' r='7' fill='#fff' stroke='${c}' stroke-width='1.4'/>`)
          .join(""),
    ),
  },
  {
    id: "graph",
    name: "Graph",
    desc: "Nodes and edges: networks, automata.",
    libs: "positioning",
    needs: [{ kind: "library", name: "positioning" }],
    source: [
      "% A graph: named nodes placed relative to each other, then the edges.",
      "\\begin{tikzpicture}[",
      "    node distance=12mm,",
      "    vertex/.style={circle, draw=lilia-ink, minimum size=7mm},",
      "  ]",
      "  \\node[vertex, draw=lilia-accent, fill=lilia-accent-soft] (a) {$a$};",
      "  \\node[vertex, below left=of a] (b) {$b$};",
      "  \\node[vertex, below right=of a] (c) {$c$};",
      "  \\node[vertex, below=of b] (d) {$d$};",
      "  \\node[vertex, below=of c] (e) {$e$};",
      "  % The edges",
      "  \\draw (a) -- (b) (a) -- (c) (b) -- (d) (d) -- (e) (e) -- (c) (a) -- (e);",
      "\\end{tikzpicture}",
    ].join("\n"),
    thumb: svg(
      [
        [30, 30, 80, 18],
        [80, 18, 130, 34],
        [30, 30, 52, 78],
        [52, 78, 108, 80],
        [108, 80, 130, 34],
        [80, 18, 108, 80],
      ]
        .map(([a, b, c, d]) => line(a, b, c, d))
        .join("") +
        [
          [30, 30],
          [80, 18],
          [130, 34],
          [52, 78],
          [108, 80],
        ]
          .map(
            ([x, y], i) =>
              `<circle cx='${x}' cy='${y}' r='8' fill='${i === 1 ? A : "#fff"}' stroke='${i === 1 ? A : K}' stroke-width='1.4'/>`,
          )
          .join(""),
    ),
  },
  {
    id: "blank",
    name: "Blank",
    desc: "An empty tikzpicture, for your own.",
    libs: "nothing added",
    needs: [],
    source: [
      "% Your figure. Theme colours: lilia-ink, lilia-accent, lilia-accent-soft, lilia-chapter.",
      "\\begin{tikzpicture}",
      "  % Draw here, e.g. \\draw[->, lilia-accent] (0,0) -- (2,1);",
      "  ",
      "\\end{tikzpicture}",
    ].join("\n"),
    thumb: svg(
      `<rect x='20' y='16' width='120' height='68' rx='6' fill='none' stroke='#8A93A0' stroke-width='1.2' stroke-dasharray='4 4'/><text x='80' y='54' font-family='monospace' font-size='9' fill='#5B6470' text-anchor='middle'>\\begin{tikzpicture}</text>`,
    ),
  },
];

export function templateById(id: string): TikzTemplate | undefined {
  return TIKZ_TEMPLATES.find((t) => t.id === id);
}
